import type { DeviceRow } from "./devices.tsx";
import { tagLabel } from "./tableState.ts";

export const PLACEMENT_FILTER_COLUMNS = ["name", "hostname", "organisation", "department", "tags", "status"] as const;

export type PlacementFilterColumn = (typeof PLACEMENT_FILTER_COLUMNS)[number];

export type PlacementFilters = {
  readonly values: ReadonlyMap<PlacementFilterColumn, ReadonlySet<string>>;
};

export const NO_PLACEMENT_FILTERS: PlacementFilters = { values: new Map() };

export type PlacementSortDirection = "asc" | "desc";

export type PlacementSort = { readonly column: PlacementFilterColumn; readonly direction: PlacementSortDirection };

export const DEFAULT_PLACEMENT_SORT: PlacementSort = { column: "name", direction: "asc" };

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function tags(device: DeviceRow): string {
  return device.tags.length === 0 ? "None" : device.tags.map(tagLabel).join(", ");
}

export function placementFacetValue(row: DeviceRow, column: PlacementFilterColumn): string {
  switch (column) {
    case "name":
      return row.name;
    case "hostname":
      return row.hostname;
    case "organisation":
      return row.organisationName ?? "";
    case "department":
      return row.departmentName ?? "";
    case "tags":
      return tags(row);
    case "status":
      return row.status;
  }
}

type RowFacets = ReadonlyMap<PlacementFilterColumn, string>;

function rowFacets(row: DeviceRow): RowFacets {
  return new Map(PLACEMENT_FILTER_COLUMNS.map((column) => [column, placementFacetValue(row, column)]));
}

function passes(facets: RowFacets, column: PlacementFilterColumn, filters: PlacementFilters): boolean {
  const selected = filters.values.get(column);
  if (!selected || selected.size === 0) return true;
  return selected.has(facets.get(column) ?? "");
}

export function filterPlacementRows(rows: readonly DeviceRow[], filters: PlacementFilters): DeviceRow[] {
  return rows.filter((row) => {
    const facets = rowFacets(row);
    return PLACEMENT_FILTER_COLUMNS.every((column) => passes(facets, column, filters));
  });
}

export type PlacementFilterOption = { readonly value: string; readonly label: string; readonly count: number };

export type PlacementColumnFacet = {
  readonly options: readonly PlacementFilterOption[];
};

export type PlacementFacets = {
  readonly rows: readonly DeviceRow[];
  readonly columns: ReadonlyMap<PlacementFilterColumn, PlacementColumnFacet>;
};

export function computePlacementFacets(rows: readonly DeviceRow[], filters: PlacementFilters): PlacementFacets {
  const counts = new Map<PlacementFilterColumn, Map<string, number>>(PLACEMENT_FILTER_COLUMNS.map((column) => [column, new Map()]));
  const passing: DeviceRow[] = [];
  for (const row of rows) {
    const facets = rowFacets(row);
    const failed = PLACEMENT_FILTER_COLUMNS.filter((column) => !passes(facets, column, filters));
    if (failed.length === 0) passing.push(row);
    for (const column of PLACEMENT_FILTER_COLUMNS) {
      const counted = failed.length === 0 || (failed.length === 1 && failed[0] === column);
      const tally = counts.get(column);
      if (!tally) continue;
      const value = facets.get(column) ?? "";
      tally.set(value, (tally.get(value) ?? 0) + (counted ? 1 : 0));
    }
  }
  const columns = new Map<PlacementFilterColumn, PlacementColumnFacet>();
  for (const column of PLACEMENT_FILTER_COLUMNS) {
    const tally = counts.get(column) ?? new Map<string, number>();
    columns.set(column, {
      options: [...tally.keys()].sort((left, right) => collator.compare(left, right)).map((value) => ({
        value,
        label: value.length === 0 ? "(empty)" : value,
        count: tally.get(value) ?? 0,
      })),
    });
  }
  return { rows: passing, columns };
}

function filterSignature(filters: PlacementFilters): string {
  const parts: string[] = [];
  for (const column of PLACEMENT_FILTER_COLUMNS) {
    const selected = filters.values.get(column);
    if (!selected || selected.size === 0) continue;
    parts.push(column, ...[...selected].sort());
  }
  return parts.join("\u0000");
}

export function createPlacementFacetMemo(compute: typeof computePlacementFacets = computePlacementFacets) {
  let last: { rows: readonly DeviceRow[]; signature: string; facets: PlacementFacets } | null = null;
  return (rows: readonly DeviceRow[], filters: PlacementFilters): PlacementFacets => {
    const signature = filterSignature(filters);
    if (last && last.rows === rows && last.signature === signature) return last.facets;
    const facets = compute(rows, filters);
    last = { rows, signature, facets };
    return facets;
  };
}

export function togglePlacementFilter(filters: PlacementFilters, column: PlacementFilterColumn, value: string): PlacementFilters {
  const values = new Map(filters.values);
  const selected = new Set(values.get(column) ?? []);
  if (selected.has(value)) selected.delete(value);
  else selected.add(value);
  if (selected.size === 0) values.delete(column);
  else values.set(column, selected);
  return { values };
}

export function selectedPlacementFilterCount(filters: PlacementFilters, column: PlacementFilterColumn): number {
  return filters.values.get(column)?.size ?? 0;
}

export function hasPlacementFilters(filters: PlacementFilters): boolean {
  return [...filters.values.values()].some((selected) => selected.size > 0);
}

export function sortPlacementRows(rows: readonly DeviceRow[], sort: PlacementSort): DeviceRow[] {
  const copy = [...rows];
  const direction = sort.direction === "asc" ? 1 : -1;
  copy.sort((left, right) => collator.compare(placementFacetValue(left, sort.column), placementFacetValue(right, sort.column)) * direction);
  return copy;
}

export const PLACEMENT_COLUMN_LABELS: Readonly<Record<PlacementFilterColumn, string>> = {
  name: "Name",
  hostname: "Hostname",
  organisation: "Organisation",
  department: "Department",
  tags: "Tags",
  status: "Status",
};
